// Tab state for the closeable multi-tab admin shell.
// Reference: TVP2 §9 image2 — each opened module page becomes a tab next
// to existing tabs; tabs can be closed via the × button; tab state
// persists in sessionStorage so a refresh preserves the open set.

import { defaultLabelForPath } from "./routeIcons";

export type TabRecord = {
  /** Route path — used for navigate() AND as the unique tab key. */
  path: string;
  /** Display label shown in the tab. */
  label: string;
  /** Key into the routeIcon registry — kept as a string (not a ReactNode)
   *  so the record is JSON-serializable for sessionStorage. */
  iconKey: string;
};

// Bump this version when canonical tab labels change so persisted
// records under the old key are ignored on read, forcing a fresh
// fallback. v1 → v2 was the "DashBoard" → "Dashboard" rename;
// v2 → v3 forces another flush after the sidebar label was also
// updated to "Dashboard" (browsers that already cached v2 with a
// "DashBoard" record need a second flush).
const STORAGE_KEY = "findgeeks.admin.tabs.v3";

/** Read the persisted tab list from sessionStorage. Returns [] on empty,
 *  malformed, or storage-unavailable.
 *
 *  IMPORTANT — label freshness: every record's `label` is OVERWRITTEN at
 *  read time with `defaultLabelForPath(record.path)`. This means a stored
 *  stale label (e.g. an old "DashBoard" persisted before the label was
 *  renamed to "Dashboard") gets corrected on every page load with zero
 *  manual cache flushing. The sessionStorage retains its old value but
 *  it's never displayed.
 */
export function readTabs(): TabRecord[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Defensive shape-check; drop anything that doesn't look like a TabRecord,
    // then refresh the label from the canonical source so stale records can't
    // outlive a label rename.
    return parsed
      .filter(
        (t): t is TabRecord =>
          typeof t === "object" &&
          t !== null &&
          typeof t.path === "string" &&
          typeof t.label === "string" &&
          typeof t.iconKey === "string",
      )
      .map((t) => ({ ...t, label: defaultLabelForPath(t.path) }));
  } catch {
    return [];
  }
}

/** Persist the tab list. Silent on storage failures (private mode etc.). */
export function writeTabs(tabs: TabRecord[]): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tabs));
  } catch {
    // ignore — sessionStorage may be disabled
  }
}

/** Pure helper: return a NEW tab list with the given tab opened (no-op if
 *  already present, idempotent on path). */
export function openTab(tabs: TabRecord[], next: TabRecord): TabRecord[] {
  if (tabs.some((t) => t.path === next.path)) return tabs;
  return [...tabs, next];
}

/** Pure helper: return a NEW tab list with the given path removed.
 *  Always preserves at least one tab — if the closed tab was the only
 *  one, returns a single-element list with the dashboard fallback. */
export function closeTab(
  tabs: TabRecord[],
  path: string,
  fallback: TabRecord,
): TabRecord[] {
  const next = tabs.filter((t) => t.path !== path);
  return next.length > 0 ? next : [fallback];
}

/** Pure helper: given the current path + tab list, return the path the
 *  user should navigate to AFTER closing `closingPath`. Picks the
 *  neighbor on the right (or left if closing the rightmost). Returns
 *  null if `closingPath` isn't the active path (no nav needed). */
export function nextActivePath(
  tabs: TabRecord[],
  activePath: string,
  closingPath: string,
  fallback: string,
): string | null {
  if (closingPath !== activePath) return null;
  const idx = tabs.findIndex((t) => t.path === closingPath);
  if (idx === -1) return null;
  // Neighbor preference: right, else left, else fallback.
  const right = tabs[idx + 1];
  const left = tabs[idx - 1];
  return right?.path ?? left?.path ?? fallback;
}
